/**
 * Influencer Link — data-layer client.
 *
 * Thin typed wrapper over `apiClient` for the `/api/v1/influencer-link`
 * surface (feature #116). Mirrors the contract declared in
 * `apps/api/src/routes/influencer-link.ts`.
 *
 * Every influencer can have N short, unique slug-backed links. This file
 * exposes the CRUD + validation helpers used by `InfluencerLinkPage` and
 * its vitest harness. No React / TanStack imports so the file is reusable
 * from server-side smoke scripts.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Shared constants / patterns — aligned with the API contract.
// Short-link host is fixed per brand ("go.trackerity.com"). The slug is
// 8 URL-safe chars. `ID_RE` mirrors `[A-Za-z0-9_-]{1,64}` from the worker.
// ---------------------------------------------------------------------------

export const SHORT_LINK_HOST = "go.trackerity.com";
export const SHORT_LINK_BASE = `https://${SHORT_LINK_HOST}`;
export const SLUG_LEN = 8;
export const SLUG_RE = /^[A-Za-z0-9_-]{8}$/;
export const ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
export const MAX_URL_LEN = 2048;

// ---------------------------------------------------------------------------
// Row shape — what `GET /by-influencer/:id` returns for each link. Mirrors
// `InfluencerLinkRow` in the API so the JSON round-trip is type-safe.
// ---------------------------------------------------------------------------

export interface InfluencerLinkRow {
	id: string;
	tenant_id: string;
	influencer_id: string;
	campaign_id: string | null;
	slug: string;
	dest_url: string;
	created_at: number;
	revoked_at: number | null;
}

export interface InfluencerLinkListResponse {
	influencer_id: string;
	count: number;
	links: InfluencerLinkRow[];
}

export interface InfluencerLinkCreateResponse {
	link: InfluencerLinkRow;
	attempts: number;
}

export interface InfluencerLinkRevokeResponse {
	link: InfluencerLinkRow;
	revoked: boolean;
}

// ---------------------------------------------------------------------------
// Pure helpers — reused by the component and the vitest harness.
// ---------------------------------------------------------------------------

export function buildShortUrl(slug: string): string {
	return `${SHORT_LINK_BASE}/${slug}`;
}

export function isLinkRevoked(row: InfluencerLinkRow): boolean {
	return row.revoked_at !== null;
}

// ---------------------------------------------------------------------------
// Form validation — hand-rolled `.safeParse` shape mirroring Zod. Keeps the
// dash zero-dep on zod for this feature while still giving the UI a familiar
// validation contract.
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export interface CreateLinkFormInput {
	influencerId: string;
	destinationUrl: string;
	customSlug?: string;
}

export interface CreateLinkFormParsed {
	influencerId: string;
	destinationUrl: string;
	customSlug: string | null;
}

/**
 * Validate a destination URL. Accepts only http(s) URLs with a non-empty
 * host. Matches the worker's `isValidDestUrl` contract so we never surface
 * a 400 after the client check passes.
 */
export function isValidHttpUrl(raw: unknown): raw is string {
	if (typeof raw !== "string") return false;
	const trimmed = raw.trim();
	if (trimmed.length === 0 || trimmed.length > MAX_URL_LEN) return false;
	try {
		const u = new URL(trimmed);
		if (u.protocol !== "http:" && u.protocol !== "https:") return false;
		if (!u.hostname || u.hostname.length === 0) return false;
		return true;
	} catch {
		return false;
	}
}

export function isValidInfluencerId(raw: unknown): raw is string {
	return typeof raw === "string" && ID_RE.test(raw);
}

export function isValidOptionalSlug(
	raw: unknown,
): raw is string | null | undefined {
	if (raw === undefined || raw === null) return true;
	if (typeof raw !== "string") return false;
	if (raw.trim().length === 0) return true;
	return SLUG_RE.test(raw.trim());
}

export const createLinkFormSchema = {
	safeParse(input: CreateLinkFormInput): ParseResult<CreateLinkFormParsed> {
		const errors: Record<string, string> = {};

		const influencerId = (input.influencerId ?? "").trim();
		if (influencerId.length === 0) {
			errors.influencerId = "Influencer ID is required.";
		} else if (!ID_RE.test(influencerId)) {
			errors.influencerId =
				"Influencer ID must be 1-64 URL-safe characters (letters, digits, _ or -).";
		}

		const destinationUrl = (input.destinationUrl ?? "").trim();
		if (destinationUrl.length === 0) {
			errors.destinationUrl = "Destination URL is required.";
		} else if (!isValidHttpUrl(destinationUrl)) {
			errors.destinationUrl = "Enter a valid http(s) URL (for example https://example.com/landing).";
		}

		let customSlug: string | null = null;
		if (typeof input.customSlug === "string") {
			const slugRaw = input.customSlug.trim();
			if (slugRaw.length > 0) {
				if (!SLUG_RE.test(slugRaw)) {
					errors.customSlug = `Custom slug must be exactly ${SLUG_LEN} URL-safe characters (letters, digits, _ or -).`;
				} else {
					customSlug = slugRaw;
				}
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return {
			success: true,
			data: {
				influencerId,
				destinationUrl,
				customSlug,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Remote operations — one function per endpoint. All return parsed JSON in
// the same shape the worker emits.
// ---------------------------------------------------------------------------

const BASE = "/influencer-link";

export interface CreateLinkArgs {
	influencerId: string;
	destinationUrl: string;
	/** Optional 8-char custom slug. Omit to let the worker mint one. */
	customSlug?: string | null;
	/** Optional campaign binding (not exposed by the v1 page but supported). */
	campaignId?: string | null;
}

/**
 * Create a link for an influencer. Returns the created row. 409 slug
 * collisions bubble up as `ApiError` so the form can surface them inline.
 */
export async function createLink(
	args: CreateLinkArgs,
): Promise<InfluencerLinkRow> {
	const body: Record<string, unknown> = {
		influencer_id: args.influencerId,
		dest_url: args.destinationUrl,
	};
	if (args.customSlug && args.customSlug.length > 0) {
		body.slug = args.customSlug;
	}
	if (args.campaignId && args.campaignId.length > 0) {
		body.campaign_id = args.campaignId;
	}
	const res = await apiClient.post<InfluencerLinkCreateResponse>(BASE, body);
	return res.link;
}

/**
 * List all links owned by an influencer. 404 from the worker is normalized
 * to an empty list so the table renders the empty state without throwing.
 */
export async function fetchLinksByInfluencer(
	influencerId: string,
): Promise<InfluencerLinkRow[]> {
	try {
		const res = await apiClient.get<InfluencerLinkListResponse>(
			`${BASE}/by-influencer/${encodeURIComponent(influencerId)}`,
		);
		return res.links ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

/**
 * Revoke (soft-delete) a link. Idempotent. The worker also returns the row
 * on a second revoke so the UI can re-render the revoked badge.
 */
export async function revokeLink(id: string): Promise<InfluencerLinkRow> {
	const res = await apiClient.post<InfluencerLinkRevokeResponse>(
		`${BASE}/${encodeURIComponent(id)}/revoke`,
		{},
	);
	return res.link;
}

/** Hard-delete a link. Silently no-ops on 404 to keep the UI forgiving. */
export async function deleteLink(id: string): Promise<void> {
	try {
		await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return;
		throw err;
	}
}

export const influencerLinkQueryKeys = {
	all: ["influencer-link"] as const,
	byInfluencer: (influencerId: string) =>
		[...influencerLinkQueryKeys.all, "by-influencer", influencerId] as const,
};
